import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { LeakGuardService } from "@/src/services/leakGuard/LeakGuardService";

// --- Types ---

export interface SastScannerOption {
  id: string;
  name: string;
  enabled: boolean;
}

interface LeakGuardState {
  // Common UI State
  selectedTab: string;

  // Basic Scan State
  basicScanLoading: boolean;
  basicScanResults: any[];
  basicScanPdfUrl: string | null;
  basicScanError: string | null;

  // Custom Scan State (Standard/Advanced)
  customScanLoading: boolean;
  customScanResult: any | null; // Stores the object with pdf_link
  customScanError: string | null;

  // Scanner Options (Persist selections between navigations if desired)
  sastScanners: SastScannerOption[];
  selectedSastScanners: string[];

  // NEW: Running Scan Type Tracking
  runningScanType: "Basic" | "Custom" | null;
  runningSastType: "file" | "zip" | "github" | "text" | null;

  selectedFile: File | null;  // NEW
  selectedFileType: "file" | "zip" | "github" | "text" | null;  // NEW
  selectedTextInput: string;  // NEW for text input
  
}

const initialState: LeakGuardState = {
  selectedTab: "basic",

  // Basic
  basicScanLoading: false,
  basicScanResults: [],
  basicScanPdfUrl: null,
  basicScanError: null,

  // Custom
  customScanLoading: false,
  customScanResult: null,
  customScanError: null,

  selectedFile: null,  // NEW
  selectedFileType: null,  // NEW
  selectedTextInput: "",  // NEW

  // Options
  sastScanners: [
    { id: "email", name: "Email", enabled: true },
    { id: "password", name: "Password", enabled: false },
    { id: "username", name: "Username", enabled: false },
    { id: "domain", name: "Domain", enabled: false },
    { id: "ip_address", name: "IP Address", enabled: false },
    { id: "address", name: "Address", enabled: false },
    { id: "phone", name: "Phone", enabled: false },
    { id: "vin", name: "VIN", enabled: false },
  ],
  selectedSastScanners: ["email"],

  // NEW: Initialize running scan tracking
  runningScanType: null,
  runningSastType: null,
};

// --- Async Thunks ---

// 1. Basic Scan Thunk
export const runBasicScan = createAsyncThunk(
  "leakGuard/runBasicScan",
  async (
    payload: {
      type: string;
      value: File | string;
      dataToSend?: string;
      scanType?: "file" | "zip" | "github" | "text";
    },
    { rejectWithValue }
  ) => {
    try {
      let response;
      if (
        (payload.type === "file" || payload.type === "zip") &&
        payload.value instanceof File
      ) {
        response = await LeakGuardService.scanPassword(
          { file: payload.value, type: "Basic" },
          {},
          true
        );
      } else if (payload.type === "text" && payload.dataToSend) {
        const formData = new FormData();
        formData.append("text", payload.dataToSend);
        response = await LeakGuardService.TextHIBP(formData, {}, true);
      }

      if (!response) throw new Error("No response from server");

      // Check if the API response indicates failure
      if (response && response.data.success === false) {
        return rejectWithValue(response.data.message || "Scan failed");
      }

      // Handle specific backend error messages that come as 200 OK but with error text
      if (response?.data?.message?.toLowerCase() === "no password column found") {
        return rejectWithValue("No password column found");
      }

      return response.data;
    } catch (error: any) {
      const data = error.response?.data;
      const message =
        data?.detail?.message ||
        data?.message ||
        "Basic scan failed";
      return rejectWithValue(message);
    }
  }
);

// 2. Custom Scan Thunk
export const runCustomScan = createAsyncThunk(
  "leakGuard/runCustomScan",
  async (
    payload: {
      type: string;
      value: File | string;
      dataToSend?: string;
      selectedScanners: string[];
      scanType?: "file" | "zip" | "github" | "text";
    },
    { rejectWithValue }
  ) => {
    try {
      let response;
      const { type, value, dataToSend, selectedScanners } = payload;

      if ((type === "file" || type === "zip") && value instanceof File) {
        const formData = new FormData();
        formData.append("file", value);
        formData.append("type", "Custom");
        selectedScanners.forEach((option) =>
          formData.append("scan_options", option)
        );
        response = await LeakGuardService.Dehased(formData, {}, true);
      } else if (type === "text" && dataToSend) {
        const formData = new FormData();
        formData.append("text", dataToSend);
        selectedScanners.forEach((option) =>
          formData.append("scan_options", option)
        );
        response = await LeakGuardService.TextDehased(formData, {}, true);
      }

      if (!response) throw new Error("No response from server");
      
      // Check if the API response indicates failure
      if (response && response.success === false) {
        return rejectWithValue(response.message || "Scan failed");
      }
      
      return response; // Custom scan returns direct object usually
    } catch (error: any) {
      const data = error.response?.data;
      const message =
        data?.detail?.message ||
        data?.message ||
        "Custom scan failed";
      return rejectWithValue(message);
    }
  }
);

const leakGuardSlice = createSlice({
  name: "leakGuard",
  initialState,
  reducers: {
    setLgTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
     setSelectedFile: (
    state,
    action: PayloadAction<{
      file: File | null;
      fileType: "file" | "zip" | "github" | "text";
      textInput?: string;
    }>
  ) => {
    state.selectedFile = action.payload.file;
    state.selectedFileType = action.payload.fileType;
    if (action.payload.textInput !== undefined) {
      state.selectedTextInput = action.payload.textInput;
    }
  },
  
  // NEW: Clear selected file
  clearSelectedFile: (state) => {
    state.selectedFile = null;
    state.selectedFileType = null;
    state.selectedTextInput = "";
  },
    setSastScanners: (state, action: PayloadAction<SastScannerOption[]>) => {
      state.sastScanners = action.payload;
    },
    setSelectedSastScanners: (state, action: PayloadAction<string[]>) => {
      state.selectedSastScanners = action.payload;
    },
    resetLeakGuardState: (state) => {
      // Resets results/errors but keeps options/tabs if preferred,
      // or reset everything to initialState
      state.basicScanResults = [];
      state.basicScanPdfUrl = null;
      state.basicScanError = null;
      state.basicScanLoading = false;
      state.selectedFile=null;
      state.customScanResult = null;
      state.customScanError = null;
      state.customScanLoading = false;
    },
    clearBasicResults: (state) => {
      state.basicScanResults = [];
      state.basicScanPdfUrl = null;
      state.basicScanError = null;
    },
    clearCustomResults: (state) => {
      state.customScanResult = null;
      state.customScanError = null;
    },
    // NEW: Set running scan type
    setRunningScanType: (
      state,
      action: PayloadAction<{
        scanType: "Basic" | "Custom" | null;
        sastType: "file" | "zip" | "github" | "text" | null;
      }>
    ) => {
      state.runningScanType = action.payload.scanType;
      state.runningSastType = action.payload.sastType;
    },
    // NEW: Clear running scan type
    clearRunningScanType: (state) => {
      state.runningScanType = null;
      state.runningSastType = null;
    },
  },
extraReducers: (builder) => {
    // ============================================================
    // 1. BASIC SCAN HANDLERS
    // ============================================================
    
    // Pending
    builder.addCase(runBasicScan.pending, (state, action) => {
      state.basicScanLoading = true;
      state.basicScanError = null;
      state.basicScanResults = [];
      state.basicScanPdfUrl = null;

      // Set the running state so UI knows what is happening
      const scanType = (action.meta.arg as any).scanType || "file";
      state.runningScanType = "Basic";
      state.runningSastType = scanType;
    });

    // Fulfilled (Merged Data Processing + File Clearing)
    builder.addCase(runBasicScan.fulfilled, (state, action) => {
      state.basicScanLoading = false;

      // --- 1. Process Data Logic ---
      const data = action.payload;
      
      if (data.pdf_link) {
        state.basicScanPdfUrl = data.pdf_link;
      }

      const results = data.results || [];
      const processedResults = results.map((item: any) => ({
        fileName: item.filename,
        staticsData: {
          criticalCount: item.result?.report?.statics?.critical || 0,
          highCount: item.result?.report?.statics?.high || 0,
          lowCount: item.result?.report?.statics?.low || 0,
          mediumCount: item.result?.report?.statics?.medium || 0,
          securedCount: item.result?.report?.statics?.secured || 0,
        },
        tenMostSecure: item.result?.report?.Ten_most_secure_passwords || [],
        tenMostInSecure: item.result?.report?.Ten_most_insecure_passwords || [],
        data: item.result?.report?.data || [],
      }));

      state.basicScanResults = processedResults;

      // --- 2. Clear File Storage (The New Functionality) ---
      // We clear this so the file input resets after a successful scan
      state.selectedFile = null;
      state.selectedFileType = null;
      state.selectedTextInput = "";

      // --- 3. Clear Running Status ---
      state.runningScanType = null;
      state.runningSastType = null;
    });

    // Rejected
    builder.addCase(runBasicScan.rejected, (state, action) => {
      state.basicScanLoading = false;
      state.basicScanError = action.payload as string || action.error.message || "Basic scan failed";

      // NOTE: We do NOT clear selectedFile here. 
      // This allows the user to fix the error and click "Scan" again without re-uploading.

      state.runningScanType = null;
      state.runningSastType = null;
    });

    // ============================================================
    // 2. CUSTOM SCAN HANDLERS
    // ============================================================

    // Pending
    builder.addCase(runCustomScan.pending, (state, action) => {
      state.customScanLoading = true;
      state.customScanError = null;
      state.customScanResult = null;

      const scanType = (action.meta.arg as any).scanType || "file";
      state.runningScanType = "Custom";
      state.runningSastType = scanType;
    });

    // Fulfilled
    builder.addCase(runCustomScan.fulfilled, (state, action) => {
      state.customScanLoading = false;
      state.customScanResult = action.payload; // Contains pdf_link

      // Clear File Storage
      state.selectedFile = null;
      state.selectedFileType = null;
      state.selectedTextInput = "";

      // Clear Running Status
      state.runningScanType = null;
      state.runningSastType = null;
    });

    // Rejected
    builder.addCase(runCustomScan.rejected, (state, action) => {
      state.customScanLoading = false;
      state.customScanError = action.payload as string || action.error.message || "Custom scan failed";

      // NOTE: We do NOT clear selectedFile here either.

      state.runningScanType = null;
      state.runningSastType = null;
    });
},

});

export const {
  setLgTab,
  resetLeakGuardState,
  setSastScanners,
  setSelectedSastScanners,
  clearBasicResults,
  clearCustomResults,
  setRunningScanType,
  clearRunningScanType,
  setSelectedFile,  // NEW
  clearSelectedFile,  // NEW
} = leakGuardSlice.actions;

export default leakGuardSlice.reducer;
